"use client";
import { cn } from "@/lib/utils";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, easeInOut } from "framer-motion";

const cards = [
  {
    name: "Takeaway",
    image: "/imgs/take.jpeg",
    cta: "ORDER NOW",
    text: "Enjoy your favorite dishes wherever you are. Packed with care, ready for you to take home.",
    whatsappLink: `/menu`,
  },
  {
    name: "Dine in",
    image: "/imgs/dine.jpeg",
    cta: "Reserve a Table",
    text: "Step inside and enjoy authentic dishes served in a cozy setting. Whether it's a quiet dinner or a family gathering, every seat has a story.",
    whatsappLink: `https://wa.me/8801830000233?text=${encodeURIComponent(
      "Hi, I want to Reserve a Table."
    )}`,
  },
  {
    name: "Catering",
    image: "/imgs/catering.jpeg",
    cta: "GET IN TOUCH",
    text: "From birthdays to corporate events, let us bring our flavors to your table. Every gathering deserves our special touch.",
    whatsappLink: `https://wa.me/8801830000233?text=${encodeURIComponent(
      "Hi, I want to GET IN TOUCH."
    )}`,
  },
];

const MealSection = () => {
  const [selected, setSelected] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const cardVariants = {
    rest: {
      scale: 1,
      transition: { duration: 0.3 },
    },
    hover: {
      scale: 1.02,
      transition: { duration: 0.3 },
    },
    selected: {
      maxWidth: "709px",
      transition: {
        duration: 0.5,
        ease: easeInOut,
      },
    },
    notSelected: {
      maxWidth: "346px",
      transition: {
        duration: 0.5,
        ease: easeInOut,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.2,
      },
    },
  };

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="py-10 lg:py-[90px] max-lg:px-[30px]"
    >
      <div className="max-w-[1420px] w-full mx-auto">
        <motion.div
          variants={itemVariants}
          className="center text-center flex-col gap-2 sm:gap-5 lg:gap-8.75"
        >
          <motion.h1
            variants={itemVariants}
            className="text-[37.78px] sm:text-5xl lg:text-[75px] leading-[86%] font-bold font-amiri text-spring-wood"
          >
            More Than Just a Meal
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="max-w-[906px] w-full mx-auto text-[11.11px] sm:text-lg lg:text-[25px] leading-[127%] text-old-lace"
          >
            From cozy dinners to takeaway favorites and catering for your
            special occasions — experience our food your way.
          </motion.p>
        </motion.div>

        {/* Cards container */}
        <motion.div
          variants={containerVariants}
          className="w-full flex flex-col lg:flex-row items-center gap-2.5 mt-5"
        >
          {cards.map((card, index) => (
            <React.Fragment key={card.name}>
              {/* Desktop cards */}
              <motion.div
                variants={cardVariants}
                initial="rest"
                whileHover={!isMobile ? "hover" : "rest"}
                animate={index === selected ? "selected" : "notSelected"}
                onMouseOver={() => !isMobile && setSelected(index)}
                onClick={() => isMobile && setSelected(index)}
                className={cn(
                  "lg:block hidden h-[920px] w-full pt-[133.66px] transition-all duration-300 ease-in-out bg-cover bg-center bg-no-repeat center relative overflow-hidden"
                )}
                style={{
                  backgroundImage: `url(${card.image})`,
                }}
              >
                {/* Background overlay animation */}
                <motion.div
                  className="absolute inset-0 bg-black opacity-0"
                  animate={{ opacity: index === selected ? 0.3 : 0 }}
                  transition={{ duration: 0.5 }}
                />

                <div className="text-center relative z-10">
                  <AnimatePresence>
                    {index === selected && (
                      <motion.p
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={contentVariants}
                        className="text-sm font-medium text-old-lace mb-4"
                      >
                       Breakfast, Lunch & Dinner
                        <br />
                        11:00 AM - 2:00 AM <br />
                        Open Monday through Tuesday
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <div className="center flex-col my-[178px]">
                    <motion.h3
                      className={cn(
                        index === selected ? "text-[75px]" : "text-[50px]",
                        "font-bold font-amiri text-spring-wood leading-[100%]"
                      )}
                      animate={{
                        scale: index === selected ? 1.1 : 1,
                        transition: { duration: 0.4 },
                      }}
                    >
                      {card.name}
                    </motion.h3>

                    <AnimatePresence>
                      {index === selected && (
                        <motion.p
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={contentVariants}
                          className="text-xl font-medium text-old-lace mt-2"
                        >
                          At the Heart of Dhaka
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  <AnimatePresence>
                    {index === selected && (
                      <motion.p
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={contentVariants}
                        className="max-w-[486.18px] w-full mx-auto text-lg leading-[127%] text-old-lace"
                      >
                        {card.text}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {index === selected && (
                      <motion.a
                        href={card.whatsappLink}
                        target="_blank"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={contentVariants}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="border border-white px-[53.5px] py-5 rounded-[26px] w-[60%] center text-lg uppercase mt-24 mx-auto text-old-lace hover:bg-white/10 transition-colors duration-300"
                      >
                        {card.cta}
                      </motion.a>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>

              {/* Mobile cards - with accordion style animation */}
              <motion.div
                initial={false}
                animate={{
                  height: isMobile && selected === index ? "auto" : "263px",
                  transition: { duration: 0.5 },
                }}
                onClick={() =>
                  isMobile && setSelected(selected === index ? -1 : index)
                }
                className={cn(
                  "lg:hidden block w-full py-5 transition-all duration-300 ease-in-out bg-cover bg-center bg-no-repeat center relative overflow-hidden",
                  selected === index ? "min-h-[400px]" : "h-[263px]"
                )}
                style={{
                  backgroundImage: `url(${card.image})`,
                }}
              >
                {/* Background overlay for mobile */}
                <motion.div
                  className="absolute inset-0 bg-black opacity-0"
                  animate={{ opacity: selected === index ? 0.5 : 0.2 }}
                  transition={{ duration: 0.5 }}
                />

                <div className="text-center relative z-10 p-4">
                  <motion.p
                    className="text-[8.49px] font-medium text-old-lace"
                    animate={{ opacity: selected === index ? 1 : 0.7 }}
                  >
                    Breakfast, Lunch & Dinner
                    <br />
                    11:00 AM – 2:00 AM <br />
                    Open Monday through Sunday
                  </motion.p>

                  <div className="center flex-col my-[12px]">
                    <motion.h3
                      className={cn(
                        "text-[45.49px]",
                        "font-bold font-amiri text-spring-wood leading-[100%]"
                      )}
                      animate={{
                        scale: selected === index ? 1.1 : 1,
                        transition: { duration: 0.4 },
                      }}
                    >
                      {card.name}
                    </motion.h3>

                    <motion.p
                      className="text-[12.13px] font-medium text-old-lace"
                      animate={{ opacity: selected === index ? 1 : 0 }}
                    >
                      At the Heart of Dhaka
                    </motion.p>
                  </div>

                  <motion.div
                    initial={false}
                    animate={{
                      height: selected === index ? "auto" : 0,
                      opacity: selected === index ? 1 : 0,
                    }}
                    transition={{ duration: 0.4 }}
                    className="overflow-hidden"
                  >
                    <p className="max-w-[294.86px] w-full mx-auto text-[10.92px] leading-[127%] text-old-lace mt-2">
                      {card.text}
                    </p>
                    <motion.a
                      href={card.whatsappLink}
                      target="_blank"
                      whileTap={{ scale: 0.95 }}
                      className="border border-white px-8 py-3 w-[60%] rounded-[15.77px] center text-[10.92px] uppercase mt-7 mx-auto text-old-lace hover:bg-white/10 transition-colors duration-300"
                    >
                      {card.cta}
                    </motion.a>
                  </motion.div>
                </div>
              </motion.div>
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default MealSection;
